/**
 * ✏️ Verification Requirement Form Component
 * Create and edit verification requirement rules
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Stack,
  Grid,
  Paper,
  Divider,
  FormGroup,
  FormLabel,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import {
  adminVerificationRequirementsService,
  CreateVerificationRequirementDTO,
  UpdateVerificationRequirementDTO,
  VerificationRequirement,
  VehicleTier,
  ServiceType,
} from '@/services/adminVerificationRequirementsService';
import toast from 'react-hot-toast';

interface VerificationRequirementFormProps {
  requirement?: VerificationRequirement; // If provided, edit mode
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Available options
const VEHICLE_TIERS: VehicleTier[] = [
  'economy',
  'standard',
  'comfort',
  'premium',
  'luxury',
  'executive',
  'sports',
  'commercial',
];

const SERVICE_TYPES: ServiceType[] = ['self-drive', 'chauffeur-driven', 'airport-transfer'];

export const VerificationRequirementForm: React.FC<VerificationRequirementFormProps> = ({
  requirement,
  onSuccess,
  onCancel,
}) => {
  const isEditMode = !!requirement;

  // Form state
  const [formData, setFormData] = useState({
    rule_name: '',
    phone_verified: false,
    driver_license_verified: false,
    identity_verified: false,
    is_verified: false,
    max_booking_amount: '',
    allowed_vehicle_tiers: [] as VehicleTier[],
    allowed_service_types: [] as ServiceType[],
    min_security_deposit: '0',
    security_deposit_rate: '10',
    can_be_host: false,
    can_be_driver: false,
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Initialize form with existing data in edit mode
  useEffect(() => {
    if (requirement) {
      setFormData({
        rule_name: requirement.rule_name,
        phone_verified: requirement.phone_verified,
        driver_license_verified: requirement.driver_license_verified,
        identity_verified: requirement.identity_verified,
        is_verified: requirement.is_verified,
        max_booking_amount: requirement.max_booking_amount?.toString() || '',
        allowed_vehicle_tiers: requirement.allowed_vehicle_tiers || [],
        allowed_service_types: requirement.allowed_service_types || [],
        min_security_deposit: requirement.min_security_deposit.toString(),
        security_deposit_rate: requirement.security_deposit_rate?.toString() || '10',
        can_be_host: requirement.can_be_host,
        can_be_driver: requirement.can_be_driver,
        is_active: requirement.is_active,
      });
    }
  }, [requirement]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.rule_name.trim()) {
      newErrors.rule_name = 'Rule name is required';
    }

    if (
      !formData.phone_verified &&
      !formData.driver_license_verified &&
      !formData.identity_verified &&
      !formData.is_verified
    ) {
      newErrors.verifications = 'At least one verification requirement must be selected';
    }

    if (formData.max_booking_amount && isNaN(Number(formData.max_booking_amount))) {
      newErrors.max_booking_amount = 'Must be a valid number';
    }

    if (formData.max_booking_amount && Number(formData.max_booking_amount) < 0) {
      newErrors.max_booking_amount = 'Must be a positive number';
    }

    if (isNaN(Number(formData.min_security_deposit))) {
      newErrors.min_security_deposit = 'Must be a valid number';
    }

    if (Number(formData.min_security_deposit) < 0) {
      newErrors.min_security_deposit = 'Must be a positive number or zero';
    }

    if (isNaN(Number(formData.security_deposit_rate))) {
      newErrors.security_deposit_rate = 'Must be a valid number';
    }

    if (Number(formData.security_deposit_rate) < 0 || Number(formData.security_deposit_rate) > 100) {
      newErrors.security_deposit_rate = 'Must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateVerificationRequirementDTO | UpdateVerificationRequirementDTO = {
        rule_name: formData.rule_name,
        phone_verified: formData.phone_verified,
        driver_license_verified: formData.driver_license_verified,
        identity_verified: formData.identity_verified,
        is_verified: formData.is_verified,
        max_booking_amount: formData.max_booking_amount ? Number(formData.max_booking_amount) : null,
        allowed_vehicle_tiers: formData.allowed_vehicle_tiers.length > 0 ? formData.allowed_vehicle_tiers : null,
        allowed_service_types: formData.allowed_service_types.length > 0 ? formData.allowed_service_types : null,
        min_security_deposit: Number(formData.min_security_deposit),
        security_deposit_rate: Number(formData.security_deposit_rate),
        can_be_host: formData.can_be_host,
        can_be_driver: formData.can_be_driver,
        is_active: formData.is_active,
      };

      if (isEditMode && requirement) {
        const response = await adminVerificationRequirementsService.updateVerificationRequirement(
          requirement.id,
          payload
        );

        if (response.success) {
          toast.success('Verification requirement updated successfully');
          onSuccess?.();
        } else {
          toast.error(response.message || 'Failed to update requirement');
        }
      } else {
        const response = await adminVerificationRequirementsService.createVerificationRequirement(
          payload as CreateVerificationRequirementDTO
        );

        if (response.success) {
          toast.success('Verification requirement created successfully');
          onSuccess?.();
        } else {
          toast.error('Failed to create requirement');
        }
      }
    } catch (error: any) {
      console.error('Failed to save requirement:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save requirement';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVehicleTierToggle = (tier: VehicleTier) => {
    setFormData((prev) => ({
      ...prev,
      allowed_vehicle_tiers: prev.allowed_vehicle_tiers.includes(tier)
        ? prev.allowed_vehicle_tiers.filter((t) => t !== tier)
        : [...prev.allowed_vehicle_tiers, tier],
    }));
  };

  const handleServiceTypeToggle = (type: ServiceType) => {
    setFormData((prev) => ({
      ...prev,
      allowed_service_types: prev.allowed_service_types.includes(type)
        ? prev.allowed_service_types.filter((t) => t !== type)
        : [...prev.allowed_service_types, type],
    }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isEditMode ? '✏️ Edit Rule' : '➕ Create New Rule'}: {isEditMode ? requirement.rule_name : 'New'}
      </Typography>

      {errors.verifications && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.verifications}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Rule Name */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <TextField
            fullWidth
            label="Rule Name"
            value={formData.rule_name}
            onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
            error={!!errors.rule_name}
            helperText={errors.rule_name || 'e.g., phone_only, with_drivers_license, full_verification'}
            required
          />
        </Paper>

        {/* Required Verifications */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            Required Verifications *
          </FormLabel>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.phone_verified}
                  onChange={(e) => setFormData({ ...formData, phone_verified: e.target.checked })}
                />
              }
              label="Phone Verified"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.driver_license_verified}
                  onChange={(e) => setFormData({ ...formData, driver_license_verified: e.target.checked })}
                />
              }
              label="Driver's License Verified"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.identity_verified}
                  onChange={(e) => setFormData({ ...formData, identity_verified: e.target.checked })}
                />
              }
              label="Identity Verified (NIN/Passport)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_verified}
                  onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                />
              }
              label="Admin Approved"
            />
          </FormGroup>
        </Paper>

        {/* Booking Limits */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Booking Limits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Booking Amount"
                type="number"
                value={formData.max_booking_amount}
                onChange={(e) => setFormData({ ...formData, max_booking_amount: e.target.value })}
                error={!!errors.max_booking_amount}
                helperText={errors.max_booking_amount || 'Leave empty for unlimited'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Min Security Deposit"
                type="number"
                value={formData.min_security_deposit}
                onChange={(e) => setFormData({ ...formData, min_security_deposit: e.target.value })}
                error={!!errors.min_security_deposit}
                helperText={errors.min_security_deposit}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Security Deposit Rate"
                type="number"
                value={formData.security_deposit_rate}
                onChange={(e) => setFormData({ ...formData, security_deposit_rate: e.target.value })}
                error={!!errors.security_deposit_rate}
                helperText={errors.security_deposit_rate || 'Percentage of booking amount (0-100)'}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.01,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Allowed Vehicle Tiers */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            Allowed Vehicle Tiers
          </FormLabel>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Select none to allow all vehicle tiers
          </Typography>
          <Grid container spacing={1}>
            {VEHICLE_TIERS.map((tier) => (
              <Grid item xs={6} md={3} key={tier}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allowed_vehicle_tiers.includes(tier)}
                      onChange={() => handleVehicleTierToggle(tier)}
                      size="small"
                    />
                  }
                  label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Allowed Service Types */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            Allowed Service Types
          </FormLabel>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Select none to allow all service types
          </Typography>
          <FormGroup>
            {SERVICE_TYPES.map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={formData.allowed_service_types.includes(type)}
                    onChange={() => handleServiceTypeToggle(type)}
                  />
                }
                label={
                  type === 'self-drive' ? 'Self-Drive' :
                  type === 'chauffeur-driven' ? 'Chauffeur-Driven' :
                  type === 'airport-transfer' ? 'Airport Transfer' :
                  type
                }
              />
            ))}
          </FormGroup>
        </Paper>

        {/* Permissions */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            Special Permissions
          </FormLabel>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.can_be_host}
                  onChange={(e) => setFormData({ ...formData, can_be_host: e.target.checked })}
                />
              }
              label="Can Be Host (list vehicles for rent)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.can_be_driver}
                  onChange={(e) => setFormData({ ...formData, can_be_driver: e.target.checked })}
                />
              }
              label="Can Be Driver (drive for others)"
            />
          </FormGroup>
        </Paper>

        {/* Status */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Active"
          />
        </Paper>

        <Divider />

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" startIcon={<Cancel />} onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <Save />}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Rule'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default VerificationRequirementForm;
